import { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './Contact.css';

const Contact = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <>
      <Helmet>
        <title>Contact Us — Azamra</title>
        <meta name="description" content="Get in touch with Azamra. Reach out to our team for support, partnerships, or to learn more about our treasury and investment solutions." />
        <meta property="og:title" content="Contact Us — Azamra" />
        <meta property="og:description" content="Get in touch with Azamra. Reach out to our team for support, partnerships, or to learn more about our treasury and investment solutions." />
        <meta property="og:url" content="https://www.azamra.com/contact" />
        <link rel="canonical" href="https://www.azamra.com/contact" />
      </Helmet>
      <Header />

      {/* Contact Hero Section */}
      <section className="contact-hero-section" style={{ backgroundImage: `url('/assets/images/hero-bg1.webp')` }}>
        <div className="contact-hero-overlay" />
        <Container className="contact-hero-content">
          <Row className="justify-content-center">
            <Col lg={8} md={10} xs={12} className="text-center">
              <h1 className="contact-hero-title">Get in Touch</h1>
              <p className="contact-hero-subtitle">
                Have questions about Azamra? Our team is here to help you
                unlock the future of tokenized assets in Africa.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <Container>
          <Row className="contact-main-row">

            {/* Left: Form Card */}
            <Col lg={6} md={12} xs={12} className="contact-form-col">
              <div className="contact-form-wrap">
                <h2 className="contact-form-title">Get In Touch</h2>

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="contact-field-col">
                    <input
                      type="text"
                      name="fullName"
                      className="contact-input"
                      placeholder="Full Name"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="contact-field-col">
                    <input
                      type="email"
                      name="email"
                      className="contact-input"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="contact-field-col">
                    <input
                      type="text"
                      name="subject"
                      className="contact-input"
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="contact-field-col">
                    <textarea
                      name="message"
                      className="contact-input contact-textarea"
                      placeholder="Message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="contact-field-col">
                    <button type="submit" className="contact-submit-btn">
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </Col>

            {/* Right: Contact Info */}
            <Col lg={6} md={12} xs={12} className="contact-info-col">
              <div className="contact-info-wrap">
                <h2 className="contact-info-title">Contact Info</h2>

                <div className="contact-info-items">
                  {/* Email */}
                  <div className="contact-info-item">
                    <div className="contact-info-icon-wrap">
                      <svg className="contact-info-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <p className="contact-info-text">Info@Azamra.com</p>
                  </div>

                  {/* United States */}
                  <div className="contact-info-item">
                    <div className="contact-info-icon-wrap">
                      <span className="contact-info-flag">🇺🇸</span>
                    </div>
                    <div>
                      <p className="contact-info-location">United States</p>
                      <p className="contact-info-address">30 Wall Street, New York, NY 10005</p>
                      <p className="contact-info-phone">📞 +1 212 634 4288</p>
                    </div>
                  </div>

                  {/* Cameroon */}
                  <div className="contact-info-item">
                    <div className="contact-info-icon-wrap">
                      <span className="contact-info-flag">🇨🇲</span>
                    </div>
                    <div>
                      <p className="contact-info-location">Cameroon</p>
                      <p className="contact-info-address">Mfoundi Entrée de la Gare</p>
                      <p className="contact-info-phone">📞 + 237 693 10 22 07</p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

          </Row>
        </Container>
      </section>

      {/* CTA Banner Section */}
      <section className="cta-section">
        <Container>
          <div
            className="cta-banner"
            style={{ backgroundImage: `url('/assets/images/${isMobile ? 'cta1.webp' : 'cta.webp'}')` }}
          >
            <h2 className="cta-title">
              <span className="cta-title-bold">Azamra provides the secure infrastructure to</span>
              <span className="cta-title-regular">launch, manage, and scale tokenized assets globally</span>
            </h2>
            <div className="cta-badges">
              {/* <a href="#app-store">
                <img src="/assets/images/app-store-badge.webp" alt="Download on the App Store" className="cta-badge" />
              </a>
              <a href="#google-play">
                <img src="/assets/images/google-play-badge.webp" alt="Get it on Google Play" className="cta-badge" />
              </a> */}
              <a href="/contact" className="cta-demo-btn">Book a Demo</a>
            </div>
          </div>
        </Container>
      </section>

      <Footer />
    </>
  );
};

export default Contact;
